import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Tabs, Tab, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { showToast } from '../../hooks/useToast';

const UserComparison = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        fetchComparison();
    }, []);

    const fetchComparison = async () => {
        try {
            const response = await api.get('/users/compare');
            setData(response.data.comparison);
        } catch (error) {
            showToast('Error al cargar la comparación', 'danger');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const syncToMoodle = async (userId) => {
        if (processing) return;
        setProcessing(true);
        try {
            await api.post(`/users/sync/${userId}`);
            showToast('Usuario creado en Moodle correctamente', 'success');
            // Recargar datos localmente removiendo el usuario de la lista
            setData(prev => ({
                ...prev,
                localOnly: prev.localOnly.filter(u => u.id !== userId),
                matches: [...prev.matches, {
                    local: prev.localOnly.find(u => u.id === userId),
                    moodle: { id: 'new', email: '...', fullname: '...' } // Placeholder
                }]
            }));
        } catch (error) {
            showToast('Error al sincronizar usuario', 'danger');
        } finally {
            setProcessing(false);
        }
    };

    // Actualizar un usuario local vinculándolo con el Moodle ID
    const importFromMoodle = async (moodleUser) => {
        if (processing) return;

        // Confirmar acción
        if (!window.confirm(`¿Estás seguro de vincular al usuario de Moodle "${moodleUser.fullname}" con el usuario local del mismo nombre?`)) {
            return;
        }

        setProcessing(true);
        try {
            await api.post('/users/link-moodle-user', {
                moodleId: moodleUser.id,
                email: moodleUser.email
            });

            showToast('Usuario vinculado exitosamente', 'success');

            // Mover de "Moodle Only" a "Matches"
            setData(prev => {
                const updatedMoodleOnly = prev.moodleOnly.filter(u => u.id !== moodleUser.id);
                // No tenemos todos los datos del usuario local aquí, pero podemos simular la entrada o refrescar
                // Para simplificar y consistencia, recargamos la data completa
                return prev;
            });

            // Refrescar para ver los cambios reflejados correctamente
            fetchComparison();

        } catch (error) {
            const msg = error.response?.data?.message || 'Error al importar usuario';
            showToast(msg, 'danger');
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Analizando comparativa de usuarios...</p>
            </div>
        );
    }

    if (!data) return <Alert variant="danger">No se pudieron cargar los datos.</Alert>;

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Button variant="outline-secondary" size="sm" className="mb-2" onClick={() => navigate('/users')}>
                        <i className="bi bi-arrow-left me-1"></i> Volver
                    </Button>
                    <h2>Comparativa de Usuarios</h2>
                </div>
                <div>
                    <Button variant="primary" onClick={fetchComparison}>
                        <i className="bi bi-arrow-clockwise me-1"></i> Actualizar
                    </Button>
                </div>
            </div>

            {data.updated > 0 && (
                <Alert variant="success" className="mb-4">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Se vincularon automáticamente <strong>{data.updated}</strong> usuarios que coincidían por email.
                </Alert>
            )}

            <Tabs defaultActiveKey="local-only" className="mb-4">
                <Tab eventKey="local-only" title={<span className="text-warning">Solo en Local ({data.localOnly.length})</span>}>
                    <Card>
                        <Card.Header className="bg-white">
                            <div className="text-muted small">
                                Usuarios que existen en la base de datos local pero NO se encontraron en Moodle (por email).
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table hover responsive className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Nombre</th>
                                        <th>Email</th>
                                        <th>Email Generado</th>
                                        <th>Rol</th>
                                        <th>Acción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.localOnly.map(user => (
                                        <tr key={user.id}>
                                            <td>{user.first_name} {user.last_name}</td>
                                            <td>{user.email || <span className="text-muted fst-italic">Sin email</span>}</td>
                                            <td className="text-muted small">{user.effectiveEmail}</td>
                                            <td>
                                                <Badge bg={user.role === 'Coordinador' ? 'primary' : user.role === 'Profesor' ? 'info' : 'success'}>{user.role}</Badge>
                                            </td>
                                            <td>
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    onClick={() => syncToMoodle(user.id)}
                                                    disabled={processing}
                                                >
                                                    Crear en Moodle
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {data.localOnly.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4 text-muted">
                                                ¡Todo sincronizado! No hay usuarios huérfanos locales.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Tab>

                <Tab eventKey="moodle-only" title={<span className="text-info">Solo en Moodle ({data.moodleOnly.length})</span>}>
                    <Card>
                        <Card.Header className="bg-white">
                            <div className="text-muted small">
                                Usuarios que existen en Moodle y coinciden por nombre con local, pero tienen email distinto.
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table hover responsive className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>ID Moodle</th>
                                        <th>Nombre Completo</th>
                                        <th>Username</th>
                                        <th>Email</th>
                                        <th>Acción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.moodleOnly.map(user => (
                                        <tr key={user.id}>
                                            <td>{user.id}</td>
                                            <td>{user.fullname}</td>
                                            <td>{user.username}</td>
                                            <td>{user.email}</td>
                                            <td>
                                                <Button
                                                    size="sm"
                                                    variant="outline-success"
                                                    onClick={() => importFromMoodle(user)}
                                                    disabled={processing}
                                                    title="Vincular a usuario local por nombre"
                                                >
                                                    Importar a Local
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {data.moodleOnly.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4 text-muted">
                                                No hay usuarios extra en Moodle.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Tab>

                <Tab eventKey="matches" title={<span className="text-success">Coincidencias ({data.matches.length})</span>}>
                    <Card>
                        <Card.Header className="bg-white">
                            <div className="text-muted small">
                                Usuarios correctamente vinculados entre Local y Moodle.
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table hover responsive className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Local</th>
                                        <th>Moodle</th>
                                        <th>Email</th>
                                        <th>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.matches.map((match, idx) => (
                                        <tr key={idx}>
                                            <td>
                                                <div>{match.local.first_name} {match.local.last_name}</div>
                                                <Badge bg={match.local.role === 'Coordinador' ? 'primary' : match.local.role === 'Profesor' ? 'info' : 'success'} className="me-2" style={{ fontSize: '0.7rem' }}>{match.local.role}</Badge>
                                                <small className="text-muted">ID: {match.local.id}</small>
                                            </td>
                                            <td>
                                                <div>{match.moodle.fullname}</div>
                                                <small className="text-muted">ID: {match.moodle.id}</small>
                                            </td>
                                            <td>{match.local.email}</td>
                                            <td>
                                                <Badge bg="success">Sincronizado</Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>
        </div>
    );
};

export default UserComparison;
